import StatusPill, { type Tone } from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime, humanize } from '@/lib/format';
import type { Connection } from '@/types/platform';

interface TokensTabProps {
  tokens: Connection[];
}

const STATUS_TONES: Record<Connection['status'], Tone> = {
  pending: 'warning',
  connected: 'success',
  needs_reconnect: 'danger',
  disconnected: 'neutral',
};

const when = (value: string | null) => formatDateTime(value) || '—';

// Token state per connection. The tokens themselves are held by the platform and never sent here.
const TokensTab = ({ tokens }: TokensTabProps) => (
  <Card>
    <CardHeader>
      <CardTitle>
        <h2>Tokens</h2>
      </CardTitle>
      <CardDescription>
        OAuth token state for each connection. The platform holds and refreshes the tokens; no
        secret is shown here.
      </CardDescription>
    </CardHeader>
    <CardContent>
      {tokens.length === 0 ? (
        <EmptyState description="This app has no connections yet." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Access token expires</TableHead>
              <TableHead>Refresh token expires</TableHead>
              <TableHead>Last refresh</TableHead>
              <TableHead>Last error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tokens.map((token) => (
              <TableRow key={token.id}>
                <TableCell className="font-medium">
                  {token.label}
                  {token.demo ? <span className="text-muted-foreground"> (demo)</span> : null}
                </TableCell>
                <TableCell>
                  <StatusPill tone={STATUS_TONES[token.status]}>
                    {humanize(token.status)}
                  </StatusPill>
                </TableCell>
                <TableCell>{token.mode}</TableCell>
                <TableCell className="text-muted-foreground">
                  {when(token.accessTokenExpiresAt)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {when(token.refreshTokenExpiresAt)}
                </TableCell>
                <TableCell className="text-muted-foreground">{when(token.refreshedAt)}</TableCell>
                <TableCell className="max-w-xs min-w-40 whitespace-normal">
                  {token.lastError ? (
                    <span className="text-xs break-words text-red-700">{token.lastError}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

export default TokensTab;
